import axios from 'axios';
import { logger } from './logger';

// Get API URL from environment or use default
const getApiUrl = () => {
  // Check for VITE_API_URL first (direct API URL without /api suffix)
  const envApiUrl = import.meta.env?.VITE_API_URL;
  
  if (envApiUrl) {
    return envApiUrl;
  }
  
  // Check for VITE_BACKEND_URL (will not add /api suffix as backend routes are direct)
  const envBackendUrl = import.meta.env?.VITE_BACKEND_URL;
  
  if (envBackendUrl) {
    logger.log('✅ Using Backend URL from env:', envBackendUrl);
    return envBackendUrl;
  }
  
  // Fallback only for true localhost development
  if (typeof window !== 'undefined') {
    const { hostname } = window.location;
    
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      logger.warn('⚠️ No VITE_API_URL/VITE_BACKEND_URL set, using localhost fallback');
      return 'http://localhost:5001/api';
    }
  }
  
  // Production without env var - use placeholder
  logger.warn('⚠️ VITE_API_URL not set. Add it in AWS Amplify Console > Environment Variables');
  return 'http://localhost:5001/api';
};

export const API_URL = getApiUrl();

// Create axios instance with default config
const axiosInstance = axios.create({
  baseURL: API_URL,
  timeout: 30000, // 30 seconds
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // Allow cookies
});

// Request interceptor to add auth token
axiosInstance.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
axiosInstance.interceptors.response.use(
  (response) => response,
  (error) => {
    // Only log actual server errors, not network connectivity issues
    if (error.response) {
      // Server responded with error status - silently handle 401 (auth errors) completely
      if (error.response.status !== 401 && error.response.status !== 404) {
        logger.error('❌ API Error:', {
          status: error.response.status,
          message: error.response.data?.message,
          url: error.config?.url
        });
      }
      // Don't log 401 errors at all - they're expected during registration/logout
    } else if (error.code !== 'ERR_NETWORK' && error.message !== 'Network Error') {
      // Only log non-network errors
      logger.error('❌ API Request Error:', error.message);
    }
    // Network errors (ERR_NETWORK) and 401s are silently handled
    
    return Promise.reject(error);
  }
);

export default axiosInstance;